import type { Transaction } from '@rocicorp/zero';
import { parseAllergenKeys } from '../allergy-matrix/allergens';
import { nextPosition, sortByPosition } from '../menus/ordering';
import type { schema } from '../zero-schema';
import type {
  ActivateAllergyMatrixArgs,
  ApproveAllergyMatrixImportArgs,
  CreateAllergyMatrixArgs,
  CreateAllergyMatrixRowArgs,
  DeleteAllergyMatrixArgs,
  DeleteAllergyMatrixRowArgs,
  DeleteAllergyMatrixSectionArgs,
  MoveAllergyMatrixRowArgs,
  RenameAllergyMatrixSectionArgs,
  UpdateAllergyMatrixArgs,
  UpdateAllergyMatrixRowArgs
} from './mutator-types';
import { zb } from './query-authz';
import { stableSequences } from './stable-values';

/**
 * Optimistic client halves of allergy-matrix-mutators.ts. Same contract as
 * client-mutators.ts: deterministic ids/sequences/timestamps from the args,
 * reads through `tx.run(zb...)`, and never a security boundary. A request the
 * server would reject (an archived matrix, a matrix not readable here) is a
 * local no-op; the server's error code reaches the caller through `.server`.
 * `updatedByUserId` is left to the server, which takes it from the session.
 */

type Tx = Transaction<typeof schema>;
type Row = {
  id: string;
  position: number;
  sectionName?: string | null;
  allergenKeys?: string | null;
};

function mutationNow(args: { now?: number | undefined }): number {
  return args.now ?? 0;
}

async function editableMatrix(tx: Tx, id: string) {
  const matrix = await tx.run(zb.allergyMatrices.where('id', '=', id).one());
  return matrix && matrix.status !== 'archived' ? matrix : undefined;
}

async function rowsOf(tx: Tx, matrixId: string): Promise<Row[]> {
  return sortByPosition(
    (await tx.run(
      zb.allergyMatrixRows.where('matrixId', '=', matrixId)
    )) as Row[]
  );
}

async function touch(tx: Tx, matrixId: string, now: number) {
  await tx.mutate.allergyMatrices.update({ id: matrixId, updatedAt: now });
}

async function activate(
  tx: Tx,
  matrix: { id: string; organizationId: string },
  now: number
) {
  const current = await tx.run(
    zb.allergyMatrices
      .where('organizationId', '=', matrix.organizationId)
      .where('status', '=', 'active')
  );
  for (const other of current) {
    if (other.id !== matrix.id) {
      await tx.mutate.allergyMatrices.update({
        id: other.id,
        status: 'archived',
        archivedAt: now,
        updatedAt: now
      });
    }
  }
  await tx.mutate.allergyMatrices.update({
    id: matrix.id,
    status: 'active',
    activatedAt: now,
    archivedAt: null,
    updatedAt: now
  });
}

export const allergyMatrixClientMutators = {
  create: async (tx: Tx, args: CreateAllergyMatrixArgs) => {
    const now = mutationNow(args);
    await tx.mutate.allergyMatrices.insert({
      id: args.id,
      organizationId: args.organizationId,
      name: args.name.trim(),
      status: 'draft',
      source: 'manual',
      customAllergens: '[]',
      footnotes: null,
      createdAt: now,
      updatedAt: now,
      globalSequence: stableSequences(args.id, 1)[0]
    });
  },
  update: async (tx: Tx, args: UpdateAllergyMatrixArgs) => {
    const matrix = await editableMatrix(tx, args.id);
    if (!matrix) {
      return;
    }
    const now = mutationNow(args);
    if (args.customAllergens !== undefined) {
      const kept = new Set(args.customAllergens.map(column => column.key));
      for (const row of await rowsOf(tx, matrix.id)) {
        const keys = parseAllergenKeys(row.allergenKeys);
        const next = keys.filter(
          key => !key.startsWith('custom_') || kept.has(key)
        );
        if (next.length !== keys.length) {
          await tx.mutate.allergyMatrixRows.update({
            id: row.id,
            allergenKeys: JSON.stringify(next),
            updatedAt: now
          });
        }
      }
    }
    await tx.mutate.allergyMatrices.update({
      id: matrix.id,
      ...(args.name !== undefined ? { name: args.name.trim() } : {}),
      ...(args.footnotes !== undefined
        ? { footnotes: args.footnotes?.trim() || null }
        : {}),
      ...(args.customAllergens !== undefined
        ? { customAllergens: JSON.stringify(args.customAllergens) }
        : {}),
      updatedAt: now
    });
  },
  activate: async (tx: Tx, args: ActivateAllergyMatrixArgs) => {
    const matrix = await tx.run(
      zb.allergyMatrices.where('id', '=', args.id).one()
    );
    if (!matrix || matrix.status === 'active') {
      return;
    }
    await activate(tx, matrix, mutationNow(args));
  },
  remove: async (tx: Tx, args: DeleteAllergyMatrixArgs) => {
    for (const row of await rowsOf(tx, args.id)) {
      await tx.mutate.allergyMatrixRows.delete({ id: row.id });
    }
    await tx.mutate.allergyMatrices.delete({ id: args.id });
  },
  renameSection: async (tx: Tx, args: RenameAllergyMatrixSectionArgs) => {
    const matrix = await editableMatrix(tx, args.matrixId);
    if (!matrix || args.from.trim() === args.to.trim()) {
      return;
    }
    const now = mutationNow(args);
    for (const row of await rowsOf(tx, matrix.id)) {
      if (row.sectionName === args.from.trim()) {
        await tx.mutate.allergyMatrixRows.update({
          id: row.id,
          sectionName: args.to.trim(),
          updatedAt: now
        });
      }
    }
    await touch(tx, matrix.id, now);
  },
  deleteSection: async (tx: Tx, args: DeleteAllergyMatrixSectionArgs) => {
    const matrix = await editableMatrix(tx, args.matrixId);
    if (!matrix) {
      return;
    }
    for (const row of await rowsOf(tx, matrix.id)) {
      if (row.sectionName === args.sectionName.trim()) {
        await tx.mutate.allergyMatrixRows.delete({ id: row.id });
      }
    }
    await touch(tx, matrix.id, mutationNow(args));
  },
  approveImport: async (tx: Tx, args: ApproveAllergyMatrixImportArgs) => {
    const existing = await tx.run(
      zb.allergyMatrices.where('id', '=', args.id).one()
    );
    if (existing) {
      return;
    }
    const now = mutationNow(args);
    await tx.mutate.allergyMatrices.insert({
      id: args.id,
      organizationId: args.organizationId,
      name: args.name.trim(),
      status: 'draft',
      source: 'ai_import',
      customAllergens: JSON.stringify(args.customAllergens),
      footnotes: args.footnotes?.trim() || null,
      createdAt: now,
      updatedAt: now,
      globalSequence: stableSequences(args.id, 1)[0]
    });
    for (const [position, row] of args.rows.entries()) {
      await tx.mutate.allergyMatrixRows.insert({
        id: row.id,
        organizationId: args.organizationId,
        matrixId: args.id,
        sectionName: row.sectionName.trim(),
        position,
        name: row.name.trim(),
        recipeId: row.recipeId ?? null,
        allergenKeys: JSON.stringify([...new Set(row.allergenKeys)]),
        note: row.note?.trim() || null,
        createdAt: now,
        updatedAt: now,
        globalSequence: stableSequences(row.id, 1)[0]
      });
    }
    await activate(tx, args, now);
  }
};

export const allergyMatrixRowClientMutators = {
  create: async (tx: Tx, args: CreateAllergyMatrixRowArgs) => {
    const matrix = await editableMatrix(tx, args.matrixId);
    if (!matrix) {
      return;
    }
    const now = mutationNow(args);
    const rows = await rowsOf(tx, matrix.id);
    await tx.mutate.allergyMatrixRows.insert({
      id: args.id,
      organizationId: matrix.organizationId,
      matrixId: matrix.id,
      sectionName: args.sectionName.trim(),
      position: nextPosition(rows),
      name: args.name.trim(),
      recipeId: args.recipeId ?? null,
      allergenKeys: JSON.stringify([...new Set(args.allergenKeys)]),
      note: args.note?.trim() || null,
      createdAt: now,
      updatedAt: now,
      globalSequence: stableSequences(args.id, 1)[0]
    });
    await touch(tx, matrix.id, now);
  },
  update: async (tx: Tx, args: UpdateAllergyMatrixRowArgs) => {
    const row = await tx.run(
      zb.allergyMatrixRows.where('id', '=', args.id).one()
    );
    if (!row || !(await editableMatrix(tx, row.matrixId))) {
      return;
    }
    const now = mutationNow(args);
    await tx.mutate.allergyMatrixRows.update({
      id: row.id,
      ...(args.sectionName !== undefined
        ? { sectionName: args.sectionName.trim() }
        : {}),
      ...(args.name !== undefined ? { name: args.name.trim() } : {}),
      ...(args.recipeId !== undefined ? { recipeId: args.recipeId } : {}),
      ...(args.allergenKeys !== undefined
        ? { allergenKeys: JSON.stringify([...new Set(args.allergenKeys)]) }
        : {}),
      ...(args.note !== undefined ? { note: args.note?.trim() || null } : {}),
      updatedAt: now
    });
    await touch(tx, row.matrixId, now);
  },
  move: async (tx: Tx, args: MoveAllergyMatrixRowArgs) => {
    const row = await tx.run(
      zb.allergyMatrixRows.where('id', '=', args.id).one()
    );
    if (!row || !(await editableMatrix(tx, row.matrixId))) {
      return;
    }
    const section = (await rowsOf(tx, row.matrixId)).filter(
      other => other.sectionName === row.sectionName
    );
    const index = section.findIndex(other => other.id === row.id);
    const neighbor = section[args.direction === 'up' ? index - 1 : index + 1];
    const position = Number(row.position ?? 0);
    if (!neighbor || neighbor.position === position) {
      return;
    }
    const now = mutationNow(args);
    await tx.mutate.allergyMatrixRows.update({
      id: row.id,
      position: neighbor.position,
      updatedAt: now
    });
    await tx.mutate.allergyMatrixRows.update({
      id: neighbor.id,
      position,
      updatedAt: now
    });
    await touch(tx, row.matrixId, now);
  },
  remove: async (tx: Tx, args: DeleteAllergyMatrixRowArgs) => {
    const row = await tx.run(
      zb.allergyMatrixRows.where('id', '=', args.id).one()
    );
    if (!row || !(await editableMatrix(tx, row.matrixId))) {
      return;
    }
    await tx.mutate.allergyMatrixRows.delete({ id: row.id });
    await touch(tx, row.matrixId, mutationNow(args));
  }
};
